import type { GridOptions, IServerSideDatasource } from "ag-grid-community";
import React from "react";

import type { AgGridReact } from "@/components/grid/AgGrid";
import { useRefreshServerSideWhen } from "./useRefreshServerSideWhen";
import { useServerSidePaginationGridProps } from "./useServerSidePaginationGridProps";

const EMPTY_REFRESH_DEPS: readonly unknown[] = [];

export type UseAdminServerSideGridParams = {
  serverSideDatasource: IServerSideDatasource;
  paginationPageSize: number;
  paginationPageSizeSelector?: readonly number[] | false;
  /**
   * External datasource inputs that AG Grid cannot see from filter/sort/page state,
   * such as debounced search text, route tabs, or parent IDs.
   */
  refreshDeps?: readonly unknown[];
  /** If true (default), avoids a duplicate purge on the first mount. */
  skipInitialRefresh?: boolean;
};

type AdminServerSideGridProps<TData> = Pick<
  GridOptions<TData>,
  | "cacheBlockSize"
  | "onPaginationChanged"
  | "pagination"
  | "paginationPageSize"
  | "paginationPageSizeSelector"
  | "rowModelType"
  | "serverSideDatasource"
> & {
  ref: React.RefObject<AgGridReact<TData> | null>;
};

export function useAdminServerSideGrid<TData>(
  params: UseAdminServerSideGridParams,
) {
  const {
    serverSideDatasource,
    paginationPageSize,
    paginationPageSizeSelector,
    refreshDeps = EMPTY_REFRESH_DEPS,
    skipInitialRefresh = true,
  } = params;

  const gridRef = React.useRef<AgGridReact<TData>>(null);
  const paginationGridProps = useServerSidePaginationGridProps<TData>({
    paginationPageSize,
    paginationPageSizeSelector,
  });

  useRefreshServerSideWhen({
    gridRef,
    deps: refreshDeps,
    skipInitial: skipInitialRefresh,
  });

  const gridProps = React.useMemo<AdminServerSideGridProps<TData>>(
    () => ({
      ref: gridRef,
      rowModelType: "serverSide",
      serverSideDatasource,
      ...paginationGridProps,
    }),
    [serverSideDatasource, paginationGridProps],
  );

  return { gridRef, gridProps };
}
